/**
 * 敌弹（BulletField）与自机弹（PlayerShots）：对象池管理 + 行为脚本。
 * 纯逻辑 + 运行时绘制，node 可直接 import（draw 才接触 renderer/Assets）。
 */
import { VIEW_W, VIEW_H, MAX_ENEMY_BULLETS, MAX_PLAYER_BULLETS } from '../config.js';
import { Pool } from '../engine/pool.js';
import { segmentHitsCircle } from '../engine/collision.js';
import { angleTo, normAngle, TAU, randRange } from '../engine/math.js';

/** 出界回收余量（px） */
const MARGIN = 32;
/** 激光默认长度（px） */
const LASER_LEN = 40;
/** 追踪弹默认转向速率（rad/s） */
const HOMING_TURN = 2.5;
/** 圆弹占位调色板（color 为非字符串索引时用） */
const PALETTE = ['#f66', '#6cf', '#fc6', '#c6f', '#6f6', '#f9f'];

function makeBullet() {
  return {
    x: 0, y: 0, vx: 0, vy: 0, r: 4,
    /** @type {string|number} sprite key 或调色板索引 */
    color: 0,
    /** @type {'round'|'laser'} */
    kind: 'round',
    /** @type {null|((b: object, dt: number, ctx: object) => void)} 自定义行为 */
    onUpdate: null,
    /** 追踪强度 0..1（>0 时每帧朝自机偏转） */
    homing: 0,
    /** 生成时的追踪强度（含难度缩放），供明灭等行为动态调制 homing 时作基准 */
    homingBase: 0,
    /** 不透明度 0..1（明灭隐身等行为驱动，默认 1） */
    alpha: 1,
    /** 视觉缩放（sprite 绘制用，默认 1 = 16px 原生；判定只看 r，与此无关） */
    scale: 1,
    /** 纯装饰弹：不参与命中与擦弹（彗尾星尘等） */
    harmless: false,
    /** 寿命秒数（0 = 无限；最后 1s 渐隐、到点回收——慢速/放置弹的消失通道） */
    ttl: 0,
    /** 出界豁免秒数（屏外发射的弹入场前不被回收；0 = 无） */
    grace: 0,
    id: 0, age: 0, _grazed: false,
  };
}

function makeShot() {
  return {
    x: 0, y: 0, vx: 0, vy: -12, damage: 1, r: 4,
    pierce: 0, homing: 0,
    /** @type {string|number} sprite key 或调色板索引 */
    color: 1,
    age: 0,
    /** 已命中目标 uid 集合：贯通弹对同一目标只结算一次（Mindustry/Q3 主流语义，
     *  贯通数 = 可命中的不同目标数；杜绝"在 boss 体内每帧重复命中"的历史 bug） */
    _hitUids: new Set(),
  };
}

/**
 * 敌弹管理：spawn/update/draw/回收/清屏转道具/命中与擦弹查询。
 */
export class BulletField {
  constructor() {
    /** @type {Pool} */
    this.pool = new Pool(MAX_ENEMY_BULLETS, makeBullet);
    this._nextId = 1;
    /** 弹速全局乘率（难度系统，gameScene 注入；1 = 基准） */
    this.speedMul = 1;
  }

  /** 当前活跃弹数 */
  get count() {
    return this.pool.activeCount;
  }

  /**
   * 生成一颗敌弹。
   * @param {{x:number, y:number, speed?:number, angle?:number, vx?:number, vy?:number,
   *   r?:number, color?:string|number, kind?:'round'|'laser', homing?:number, scale?:number,
   *   harmless?:boolean,
   *   onUpdate?:?((b:object, dt:number, ctx:object)=>void)}} opts
   *   speed+angle 与 vx+vy 二选一；angle 为弧度（0 = +x 方向）。
   * @returns {object|null} 弹对象（池满为 null）
   */
  spawn(opts) {
    const b = this.pool.obtain();
    if (!b) return null;
    b.x = opts.x;
    b.y = opts.y;
    const k = this.speedMul;
    if (opts.vx !== undefined || opts.vy !== undefined) {
      b.vx = (opts.vx || 0) * k;
      b.vy = (opts.vy || 0) * k;
    } else {
      const sp = (opts.speed || 0) * k;
      const an = opts.angle || 0;
      b.vx = Math.cos(an) * sp;
      b.vy = Math.sin(an) * sp;
    }
    b.r = opts.r !== undefined ? opts.r : 4;
    b.color = opts.color !== undefined ? opts.color : 0;
    b.kind = opts.kind || 'round';
    b.homing = opts.homing || 0;
    b.homingBase = b.homing;
    b.alpha = 1;
    b.scale = opts.scale || 1;
    b.harmless = !!opts.harmless;
    b.ttl = opts.ttl || 0;
    b.grace = opts.grace || 0;
    // 动态行为字段兜底重置（防对象池复用时沿用上一世的状态）
    b._sdx = undefined;
    b._sdy = undefined;
    b.onUpdate = opts.onUpdate || null;
    b.id = this._nextId++;
    b.age = 0;
    b._grazed = false;
    return b;
  }

  /**
   * 推进全部敌弹：自定义行为 → 追踪偏转 → 位移 → 出界回收。
   * @param {number} dt 秒
   * @param {{playerX?:number, playerY?:number}} [ctx] 供追踪弹用
   */
  update(dt, ctx = {}) {
    this.pool.forEachActive((b) => {
      b.age += dt;
      if (b.onUpdate) b.onUpdate(b, dt, ctx);
      if (!b._active) return; // onUpdate 可能已释放自身（fade/ink/burst 等），不再对死弹积分
      if (b.ttl > 0) {
        b.ttl -= dt;
        // 最后 1s 渐隐：ttl 作为 alpha 上限（与 flicker/shadow 等 alpha 行为兼容）
        if (b.ttl < 1 && b.alpha > b.ttl) b.alpha = Math.max(0, b.ttl);
        if (b.ttl <= 0) {
          this.pool.release(b);
          return;
        }
      }
      if (b.homing !== 0 && ctx.playerX !== undefined) {
        // homing > 0 朝自机偏转；< 0 背离自机偏转（timidBullet 负半周"迟疑退开"）
        const cur = Math.atan2(b.vy, b.vx);
        let want = angleTo(b.x, b.y, ctx.playerX, ctx.playerY);
        if (b.homing < 0) want += Math.PI;
        const d = normAngle(want - cur);
        const maxTurn = HOMING_TURN * Math.abs(b.homing) * dt;
        const turn = d > maxTurn ? maxTurn : d < -maxTurn ? -maxTurn : d;
        const sp = Math.hypot(b.vx, b.vy);
        const na = cur + turn;
        b.vx = Math.cos(na) * sp;
        b.vy = Math.sin(na) * sp;
      }
      b.x += b.vx * dt * 60;
      b.y += b.vy * dt * 60;
      if (b.grace > 0) {
        b.grace -= dt; // 屏外发射豁免期：不回收（流星卡统一掠向的屏外入射）
      } else if (b.x < -MARGIN || b.x > VIEW_W + MARGIN || b.y < -MARGIN || b.y > VIEW_H + MARGIN) {
        this.pool.release(b);
      }
    });
  }

  /**
   * 清屏转道具（bomb/阶段切换用）：全部活跃弹转化为 POINT 道具后回收。
   * 给 cx,cy 时收拢成一小簇（boss 阶段击破，方便拾取）；否则原地转化（bomb）。
   * @param {import('./items.js').Items} items
   * @param {number} [cx] @param {number} [cy]
   * @returns {number} 转化的弹数
   */
  clearToItems(items, cx, cy) {
    let n = 0;
    this.pool.forEachActive((b) => {
      if (cx !== undefined) {
        const a = randRange(0, TAU);
        const d = randRange(0, 36);
        items.spawn(cx + Math.cos(a) * d, cy + Math.sin(a) * d, 'POINT');
      } else {
        items.spawn(b.x, b.y, 'POINT');
      }
      this.pool.release(b);
      n++;
    });
    return n;
  }

  /**
   * 查询与圆 (x,y,r) 碰撞的敌弹（laser 按线段-圆检测）。
   * @param {number} x @param {number} y @param {number} r
   * @param {object[]} [out] 结果复用数组
   * @returns {object[]}
   */
  queryHit(x, y, r, out = []) {
    out.length = 0;
    this.pool.forEachActive((b) => {
      if (b.harmless) return;
      const rr = r + b.r;
      const dx = b.x - x;
      const dy = b.y - y;
      if (b.kind === 'laser') {
        const sp = Math.hypot(b.vx, b.vy) || 1;
        const nx = b.vx / sp;
        const ny = b.vy / sp;
        if (segmentHitsCircle(b.x - nx * LASER_LEN, b.y - ny * LASER_LEN, b.x, b.y, x, y, rr)) {
          out.push(b);
        }
      } else if (dx * dx + dy * dy <= rr * rr) {
        out.push(b);
      }
    });
    return out;
  }

  /**
   * 擦弹查询：边沿触发——弹进入 grazeRadius 时计一次擦弹，离开后可再次触发
   * （每弹总次数上限由 ScoreSystem 的 grazePerBulletMax 控制）。
   * @param {number} x @param {number} y @param {number} grazeRadius
   * @param {object[]} [out] 结果复用数组（本帧新进入擦弹圈的弹）
   * @returns {object[]}
   */
  queryGraze(x, y, grazeRadius, out = []) {
    out.length = 0;
    const rr = grazeRadius + 4; // 弹判定半径上限量级，粗筛
    this.pool.forEachActive((b) => {
      if (b.harmless) return;
      const dx = b.x - x;
      const dy = b.y - y;
      const inside = dx * dx + dy * dy <= rr * rr;
      if (inside && !b._grazed) {
        b._grazed = true;
        out.push(b);
      } else if (!inside && b._grazed) {
        b._grazed = false; // 离开后允许再次擦弹
      }
    });
    return out;
  }

  /**
   * 遍历全部活跃弹。
   * @param {(b: object) => void} fn
   */
  forEachActive(fn) {
    this.pool.forEachActive(fn);
  }

  /**
   * 绘制全部敌弹。laser 画线段+圆头；round 优先 Assets.img(color)，
   * 无素材时按调色板画占位圆。
   * @param {import('../engine/renderer.js').Renderer} renderer
   * @param {import('../engine/assets.js').Assets} [assets] 不传则一律占位圆
   */
  draw(renderer, assets) {
    this.pool.forEachActive((b) => {
      if (b.kind === 'laser') {
        const sp = Math.hypot(b.vx, b.vy) || 1;
        const nx = b.vx / sp;
        const ny = b.vy / sp;
        const ctx = renderer.ctx;
        ctx.save();
        ctx.strokeStyle = typeof b.color === 'string' ? '#f6f' : PALETTE[b.color % PALETTE.length];
        if (b.alpha !== undefined) ctx.globalAlpha = b.alpha; // ttl 渐隐等对 laser 同样生效
        ctx.lineWidth = b.r * 2;
        ctx.beginPath();
        ctx.moveTo(b.x - nx * LASER_LEN, b.y - ny * LASER_LEN);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        ctx.restore();
        renderer.circle(b.x, b.y, b.r, { fill: '#fff', alpha: b.alpha !== undefined ? b.alpha : 1 });
        return;
      }
      const img = assets && typeof b.color === 'string' ? assets.img(b.color) : undefined;
      const ba = b.alpha !== undefined ? b.alpha : 1;
      if (img) {
        renderer.sprite(img, b.x, b.y, { alpha: ba, scale: b.scale || 1 });
      } else {
        const fill = typeof b.color === 'number' ? PALETTE[b.color % PALETTE.length] : '#f66';
        renderer.circle(b.x, b.y, b.r, { fill, alpha: ba });
        renderer.circle(b.x, b.y, b.r * 0.5, { fill: '#fff', alpha: 0.6 * ba });
      }
    });
  }
}

/**
 * 自机弹管理：直线/微追踪，命中判定由 gameScene 走 Enemies.queryHit。
 */
export class PlayerShots {
  constructor() {
    /** @type {Pool} */
    this.pool = new Pool(MAX_PLAYER_BULLETS, makeShot);
  }

  /** 当前活跃弹数 */
  get count() {
    return this.pool.activeCount;
  }

  /**
   * 生成一颗自机弹。
   * @param {{x:number, y:number, vx?:number, vy?:number, damage?:number, r?:number,
   *   pierce?:number, homing?:number, color?:string|number}} opts
   * @returns {object|null}
   */
  spawn(opts) {
    const s = this.pool.obtain();
    if (!s) return null;
    s.x = opts.x;
    s.y = opts.y;
    s.vx = opts.vx || 0;
    s.vy = opts.vy !== undefined ? opts.vy : -12;
    s.damage = opts.damage !== undefined ? opts.damage : 1;
    s.r = opts.r !== undefined ? opts.r : 4;
    s.pierce = opts.pierce || 0;
    s.homing = opts.homing || 0;
    s.color = opts.color !== undefined ? opts.color : 1;
    s.age = 0;
    s._hitUids.clear();
    return s;
  }

  /**
   * 推进全部自机弹：可选追踪偏转 → 位移 → 出界回收。
   * @param {number} dt 秒
   * @param {{targetX?:number, targetY?:number}} [ctx] 追踪目标（gameScene 提供最近敌人）
   */
  update(dt, ctx = {}) {
    this.pool.forEachActive((s) => {
      s.age += dt;
      if (s.homing > 0 && ctx.targetX !== undefined) {
        const cur = Math.atan2(s.vy, s.vx);
        const want = angleTo(s.x, s.y, ctx.targetX, ctx.targetY);
        let d = want - cur;
        while (d > Math.PI) d -= TAU;
        while (d < -Math.PI) d += TAU;
        const maxTurn = HOMING_TURN * 2 * s.homing * dt;
        const turn = d > maxTurn ? maxTurn : d < -maxTurn ? -maxTurn : d;
        const sp = Math.hypot(s.vx, s.vy);
        const na = cur + turn;
        s.vx = Math.cos(na) * sp;
        s.vy = Math.sin(na) * sp;
      }
      s.x += s.vx * dt * 60;
      s.y += s.vy * dt * 60;
      if (s.x < -MARGIN || s.x > VIEW_W + MARGIN || s.y < -MARGIN || s.y > VIEW_H + MARGIN) {
        this.pool.release(s);
      }
    });
  }

  /**
   * 遍历全部活跃弹。
   * @param {(s: object) => void} fn
   */
  forEachActive(fn) {
    this.pool.forEachActive(fn);
  }

  /**
   * 绘制全部自机弹（无素材时画占位圆）。
   * @param {import('../engine/renderer.js').Renderer} renderer
   * @param {import('../engine/assets.js').Assets} [assets]
   */
  draw(renderer, assets) {
    this.pool.forEachActive((s) => {
      const img = assets && typeof s.color === 'string' ? assets.img(s.color) : undefined;
      if (img) {
        renderer.sprite(img, s.x, s.y, { rot: Math.atan2(s.vy, s.vx) + Math.PI / 2 });
      } else {
        const fill = typeof s.color === 'number' ? PALETTE[s.color % PALETTE.length] : '#6cf';
        renderer.circle(s.x, s.y, s.r, { fill });
      }
    });
  }
}
